import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Instagram } from "lucide-react";
import { immCard } from "@/components/immersive/ImmHeader";
import { useContent } from "@/context/ContentContext";

// Theme-aware footer for the Immersive 3D design (Pearl Ivory / Midnight Silk).
export const ImmersiveFooter = () => {
  const { content } = useContent();
  const s = content?.settings || {};
  const clinic = s.clinic_name || "Royal Skin Cosmetic Clinic";
  const brand = clinic.replace(/\s*cosmetic\s*clinic\s*/i, "").trim() || "Royal Skin";
  const treatments = (content?.treatments || []).slice(0, 5);

  const explore = [
    { label: "Treatments", to: "/treatments" },
    { label: "Our Doctors", to: "/doctors" },
    { label: "About", to: "/about" },
    { label: "Results Gallery", to: "/gallery" },
    { label: "Skin Journal", to: "/blog" },
    { label: "Contact", to: "/contact" },
  ];

  return (
    <footer className="relative z-10 px-4 pb-10 pt-4 sm:px-8 lg:px-16" data-testid="immersive-footer">
      <div className={`${immCard} mx-auto max-w-6xl overflow-hidden`}>
        <div className="grid gap-10 px-8 py-12 sm:px-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <p className="font-display text-3xl font-normal t-imm">{brand}</p>
            <p className="mt-1 text-[10px] font-medium uppercase tracking-[0.35em] text-[#D4AF37]">Cosmetic Clinic</p>
            <p className="t-imm-muted mt-5 max-w-xs text-sm font-light leading-relaxed">
              {s.hero_subtitle || "Physician-led skin, hair and wellness artistry in Dubai Healthcare City."}
            </p>
            {s.ig_handle && (
              <a
                href={`https://instagram.com/${s.ig_handle.replace("@", "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 text-xs font-medium text-[#D4AF37] transition-opacity hover:opacity-70"
                data-testid="immersive-footer-instagram"
              >
                <Instagram className="h-4 w-4" aria-hidden="true" /> {s.ig_handle}
              </a>
            )}
          </div>

          <nav className="md:col-span-2" aria-label="Footer">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] t-imm-muted">Explore</p>
            <ul className="mt-4 space-y-2.5">
              {explore.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="t-imm text-sm font-light transition-colors hover:text-[#D4AF37]" data-testid={`immersive-footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="md:col-span-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] t-imm-muted">Signature Treatments</p>
            <ul className="mt-4 space-y-2.5">
              {treatments.map((t) => (
                <li key={t.id}>
                  <Link to="/treatments" className="t-imm text-sm font-light transition-colors hover:text-[#D4AF37]">
                    {t.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] t-imm-muted">Visit Us</p>
            <ul className="mt-4 space-y-3 text-sm font-light t-imm-muted2">
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" />
                {s.address || "Dubai Healthcare City, Dubai"}
              </li>
              {s.phone && (
                <li>
                  <a href={`tel:${s.phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-2.5 transition-colors hover:text-[#D4AF37]" data-testid="immersive-footer-phone">
                    <Phone className="h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" /> {s.phone}
                  </a>
                </li>
              )}
              {s.email && (
                <li>
                  <a href={`mailto:${s.email}`} className="flex items-center gap-2.5 transition-colors hover:text-[#D4AF37]" data-testid="immersive-footer-email">
                    <Mail className="h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" /> {s.email}
                  </a>
                </li>
              )}
              <li className="flex items-start gap-2.5">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" />
                {s.hours || "Mon – Sat: 12:00 PM – 8:00 PM"}
              </li>
            </ul>
          </div>
        </div>

        <p aria-hidden="true" className="select-none px-8 text-center font-display text-[clamp(2.6rem,9vw,7rem)] font-normal leading-none tracking-tight t-imm opacity-[0.07]">
          {brand}
        </p>

        <div className="border-imm flex flex-wrap items-center justify-between gap-3 border-t px-8 py-5 sm:px-12">
          <p className="text-[11px] font-light t-imm-muted">
            © {new Date().getFullYear()} {clinic}. All rights reserved.
          </p>
          <p className="text-[10px] uppercase tracking-[0.25em] t-imm-muted">Dubai Healthcare City</p>
        </div>
      </div>
    </footer>
  );
};
